import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { humanizeApiError } from '@lapo/core';

/**
 * useIntegrationConfig — the shared state machine behind every Integrations-hub settings block
 * (Subscription Digest, Financial Management, Movie Enricher, Gemini Notes, …). Each block used to
 * hand-roll the same ~50 lines: config/saving/running/error/runNote state + a load effect keyed on
 * `[session, api, settingsOpen]` + save/run wrappers. That pattern had two baked-in bugs this hook
 * fixes:
 *
 *  (a) `settingsOpen` in the effect deps refired the GET on every OPEN **and CLOSE** of Settings
 *      (~14 requests per toggle). Here the config loads when `enabled` (signed in + API) becomes true,
 *      and every RISING edge of `visible` (Settings opened) triggers a background refresh — configs
 *      routinely change from other devices, so an open must show fresh data. The refresh is
 *      stale-while-revalidate: the already-loaded config stays rendered until the new one lands
 *      (offline-and-hydrate convention). CLOSE never refetches.
 *  (b) No AbortController — an unmount/sign-out mid-flight could still land setState. The hook aborts
 *      the in-flight load on unmount and when `enabled` drops, and gates every setState on the signal.
 *
 * The `load`/`save`/`run` callbacks own the actual API calls and their success toasts (they differ
 * per block); the hook owns the state transitions. `onSaveError`/`onRunError` fire for error toasts —
 * the hook records `error` either way.
 */
export interface UseIntegrationConfigOptions<T, TSave = T> {
  /** Master gate — typically `!!session && !!API_URL`. Load fires when this turns true. */
  enabled: boolean;
  /** Settings visibility. Every rising edge triggers a background refresh; close never does. */
  visible?: boolean;
  /** Fetch the config. The signal aborts on unmount/sign-out (the hook also gates setState on it). */
  load: (signal: AbortSignal) => Promise<T>;
  /** Persist the config; return the saved value (becomes the new `config`). Success toasts go here. */
  save?: (next: TSave) => Promise<T>;
  /** Called when `save` rejects (error toast); the hook records `error` regardless. */
  onSaveError?: (err: Error) => void;
  /** Run the integration now; return the run note. Success toasts / hub splices go here. */
  run?: () => Promise<string | undefined>;
  /** Called when `run` rejects (error toast); the hook records `error` regardless. */
  onRunError?: (err: Error) => void;
}

export interface UseIntegrationConfigResult<T, TSave = T> {
  config: T | null;
  setConfig: Dispatch<SetStateAction<T | null>>;
  saving: boolean;
  save: (next: TSave) => Promise<void>;
  running: boolean;
  run: () => Promise<void>;
  error: string | undefined;
  setError: Dispatch<SetStateAction<string | undefined>>;
  runNote: string | undefined;
  setRunNote: Dispatch<SetStateAction<string | undefined>>;
  /** Explicitly refetch (e.g. after an out-of-band change). Resolves when the load settles. */
  reload: () => Promise<void>;
}

export function useIntegrationConfig<T, TSave = T>(
  options: UseIntegrationConfigOptions<T, TSave>,
): UseIntegrationConfigResult<T, TSave> {
  const { enabled, visible } = options;
  const [config, setConfig] = useState<T | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [runNote, setRunNote] = useState<string | undefined>(undefined);

  // Latest callbacks in refs so the load/save/run wrappers stay referentially stable and the effects
  // don't churn on inline-closure identity.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const abortRef = useRef<AbortController | null>(null);

  const doLoad = useCallback(async (): Promise<void> => {
    if (!optionsRef.current.enabled) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const loaded = await optionsRef.current.load(ac.signal);
      if (!ac.signal.aborted) setConfig(loaded);
    } catch {
      /* load errors are silent (matches the old blocks) — the section simply stays hidden */
    }
  }, []);

  // Initial load on the rising edge of `enabled`; abort on sign-out so nothing lands afterwards.
  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      abortRef.current = null;
      return;
    }
    void doLoad();
  }, [enabled, doLoad]);

  // Rising edge of `visible` (Settings opened): background-refresh — configs change from other
  // devices, so every open refetches. The previous config stays rendered until the fresh one lands.
  // Never refetch on close (that half of the old ~14-GET bug stays fixed).
  const prevVisibleRef = useRef(!!visible);
  useEffect(() => {
    const was = prevVisibleRef.current;
    prevVisibleRef.current = !!visible;
    if (!enabled || !visible || was) return;
    void doLoad();
  }, [enabled, visible, doLoad]);

  // Abort any in-flight load on unmount.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const save = useCallback(async (next: TSave): Promise<void> => {
    const saveFn = optionsRef.current.save;
    if (!saveFn) return;
    setSaving(true);
    setError(undefined);
    try {
      const saved = await saveFn(next);
      setConfig(saved);
    } catch (err) {
      setError(humanizeApiError(err));
      optionsRef.current.onSaveError?.(err as Error);
    } finally {
      setSaving(false);
    }
  }, []);

  const run = useCallback(async (): Promise<void> => {
    const runFn = optionsRef.current.run;
    if (!runFn) return;
    setRunning(true);
    setError(undefined);
    setRunNote(undefined);
    try {
      const note = await runFn();
      setRunNote(note);
    } catch (err) {
      setError(humanizeApiError(err));
      optionsRef.current.onRunError?.(err as Error);
    } finally {
      setRunning(false);
    }
  }, []);

  return { config, setConfig, saving, save, running, run, error, setError, runNote, setRunNote, reload: doLoad };
}
